import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Trophy, TrendingUp, Crown, Medal, Award } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const API = getApiUrl();

// Rank badge component
function RankBadge({ rank }) {
  if (rank === 1) {
    return <Crown className="w-5 h-5 text-yellow-400" />;
  } else if (rank === 2) {
    return <Medal className="w-5 h-5 text-gray-400" />;
  } else if (rank === 3) {
    return <Award className="w-5 h-5 text-amber-600" />;
  }
  return <span className="w-5 text-center text-muted-foreground">{rank}</span>;
}

// Single ranking entry
function RankingEntry({ entry, showId, type }) {
  const value = type === "biz" ? entry.biz : entry.apparent;
  const label = type === "biz" ? "商才" : "みかけR";
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
      entry.rank <= 3 ? "bg-brass/10" : "bg-muted/30"
    }`}>
      <RankBadge rank={entry.rank} />
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{entry.nickname}</p>
        {showId && (
          <p className="text-xs text-muted-foreground font-mono truncate">
            {entry.player_id}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="font-data font-bold text-lg">
          {type === "biz" ? value.toFixed(1) : value.toFixed(1)}
        </p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function Ranking() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rankings, setRankings] = useState({ biz_ranking: [], apparent_ranking: [] });
  const [loading, setLoading] = useState(true);
  const [showIds, setShowIds] = useState(false);
  
  // Check if current user is admin (puriketu99)
  const isAdmin = user?.username === "puriketu99" || user?.nickname === "puriketu99";

  const fetchRankings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/rankings`, {
        params: { include_ids: showIds }
      });
      setRankings(response.data);
    } catch (error) {
      console.error("Failed to fetch rankings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRankings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIds]);

  // Handle double-click on user profile to toggle admin mode
  const handleProfileDoubleClick = () => {
    if (isAdmin) {
      setShowIds(!showIds);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 spotlight" />
      
      <Card className="w-full max-w-md bg-card/80 backdrop-blur-sm border-brass/30 relative z-10">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 text-brass">
              <Trophy className="w-6 h-6" />
            </div>
            <div className="w-9" /> {/* Spacer for centering */}
          </div>
          <CardTitle className="font-display text-2xl tracking-wider text-foreground">
            ランキング
          </CardTitle>
          
          {/* Admin indicator - double click to toggle ID display */}
          {user && (
            <div 
              className="flex items-center justify-center gap-2 text-sm text-muted-foreground cursor-pointer select-none"
              onDoubleClick={handleProfileDoubleClick}
            >
              <span>{user.nickname || user.username}</span>
              {showIds && isAdmin && (
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                  管理者モード
                </span>
              )}
            </div>
          )}
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="biz" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-muted mb-4">
              <TabsTrigger value="biz" className="font-medium text-sm flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                商才
              </TabsTrigger>
              <TabsTrigger value="apparent" className="font-medium text-sm">
                みかけR
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="biz" className="space-y-2">
              {loading ? (
                <div className="text-center text-muted-foreground py-8">
                  読み込み中...
                </div>
              ) : rankings.biz_ranking.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  まだランキングデータがありません
                </div>
              ) : (
                rankings.biz_ranking.map((entry) => (
                  <RankingEntry
                    key={entry.rank}
                    entry={entry}
                    showId={showIds}
                    type="biz"
                  />
                ))
              )}
            </TabsContent>
            
            <TabsContent value="apparent" className="space-y-2">
              {loading ? (
                <div className="text-center text-muted-foreground py-8">
                  読み込み中...
                </div>
              ) : rankings.apparent_ranking.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  まだランキングデータがありません
                </div>
              ) : (
                rankings.apparent_ranking.map((entry) => (
                  <RankingEntry
                    key={entry.rank}
                    entry={entry}
                    showId={showIds}
                    type="apparent"
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
          
          <div className="mt-6 pt-4 border-t border-border">
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="w-full"
            >
              タイトルに戻る
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
